/**
 * @file useOpenGroupRequest.ts
 * @description Один механизм «открыть группу по запросу» для всех панелей
 * (phase19.16, SB122; ревью партии SB122 — вместо трёх своих: ошибка после
 * «Сохранить», «изменить ›» из соседней вкладки, `?font=` во «Шрифтах», а
 * также `?group=` в корне групп).
 *
 * Запрос — пара «группа + ключ запроса» (`token`): номер неудачного
 * сохранения, ключ записи истории (`location.key`), сам id. Каждый ключ
 * выполняется один раз: перерисовка или смена ширины экрана (новый
 * `openGroup`) не прокручивает снова, а новый ключ к той же группе —
 * прокручивает. Нет запроса (`id` или `token` — null) — память сбрасывается.
 *
 * ```tsx
 * <SettingsGroups panel="page">
 *   <OpenGroupRequest id={firstErrorGroup} token={failedSaves || null} focusInvalid />
 * </SettingsGroups>
 * ```
 */

import { useEffect, useRef } from "react";

import { useSettingsGroupsApi, type OpenGroupOptions } from "./context";

/** Ключ запроса: число (счётчик) или строка (ключ истории, id). */
export type OpenGroupToken = string | number;

export interface OpenGroupRequestOptions extends OpenGroupOptions {
  /** Запрос выполнен (группу раскрыли): сбросить его у себя. */
  onDone?: () => void;
}

/** Ключ запроса; null — запроса нет. */
function requestKey(id: string | null, token: OpenGroupToken | null): string | null {
  return id === null || token === null ? null : `${String(token)}\u0000${id}`;
}

/**
 * Ядро: `run(id)` один раз на ключ запроса. `ready = false` — подождать
 * (группа ещё не появилась), память при этом не трогается.
 */
export function useGroupRequestOnce(id: string | null, token: OpenGroupToken | null, run: (id: string) => void, ready = true): void {
  const handled = useRef<string | null>(null);
  const latest = useRef(run);
  latest.current = run;
  const key = requestKey(id, token);
  useEffect(() => {
    if (key === null || id === null) {
      handled.current = null;
      return;
    }
    if (!ready || handled.current === key) return;
    handled.current = key;
    latest.current(id);
  }, [key, id, ready]);
}

/**
 * Раскрыть группу текущей панели по запросу (внутри `SettingsGroups`).
 * `token` по умолчанию — сам id: запрос сбрасывают, обнулив id (`onDone`).
 */
export function useOpenGroupRequest(id: string | null, token: OpenGroupToken | null = id, options: OpenGroupRequestOptions = {}): void {
  const openGroup = useSettingsGroupsApi()?.openGroup;
  const latest = useRef(options);
  latest.current = options;
  useGroupRequestOnce(
    id,
    token,
    (target) => {
      const { onDone, ...open } = latest.current;
      openGroup?.(target, open);
      onDone?.();
    },
    openGroup !== undefined,
  );
}

interface RequestProps extends OpenGroupRequestOptions {
  id: string | null;
  token?: OpenGroupToken | null;
}

/** То же компонентом — для панелей, которые сами рисуют `SettingsGroups`. */
export function OpenGroupRequest({ id, token = id, ...options }: RequestProps) {
  useOpenGroupRequest(id, token, options);
  return null;
}
